import { defineStore } from 'pinia';
import { WebsiteApiHelper } from '@/helpers/global/baseAPI.helper';

type UserInfo = {
  email?: string;
  first_name?: string;
  last_name?: string;
  has_company_with_address?: boolean;
  has_company?: boolean;
  is_verified?: boolean;
  customer_area_permissions?: CustomerAreaPermission[];
};

type CustomerAreaPermission = {
  company: {
    uid: string;
    title: string;
    slug?: string;
    url: string;
  };
  permissions: string[];
};

type CustomerCompanyAddress = {
  uid: string;
  type?: 1 | 2;
  is_default?: boolean;
  street_address?: string;
  street_address2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  shipping_method?: string;
  full_address?: string;
};

let userInfoLoadPromise: Promise<UserInfo | undefined> | null = null;

const useUserInfoStore = defineStore('user-info', {
  state: () => ({
    userInfo: undefined as UserInfo | undefined,
    companyInfo: undefined,
    companyAddresses: [] as CustomerCompanyAddress[],
    companyAddressesTotalPages: 0,
    companyAddressesCurrentPage: 1,
    companyAddressesIsEmpty: false,
  }),
  getters: {
    userDisplayName(state) {
      if (!state.userInfo) {
        return '';
      }

      return [state.userInfo.first_name, state.userInfo.last_name].filter(Boolean).join(' ')
        || state.userInfo.email
        || '';
    },
  },
  actions: {
    fetchUserInfo(forceRefresh: boolean = false) {
      if (!forceRefresh && this.userInfo !== undefined) {
        return Promise.resolve(this.userInfo);
      }
      if (!forceRefresh && userInfoLoadPromise) {
        return userInfoLoadPromise;
      }

      userInfoLoadPromise = WebsiteApiHelper.get('/user-profile/me/').then((userInfoRes) => {
        if (userInfoRes && userInfoRes.email) {
          this.userInfo = userInfoRes;
        }
        return this.userInfo;
      }).finally(() => {
        userInfoLoadPromise = null;
      });

      return userInfoLoadPromise;
    },
    fetchCompanyInfo() {
      if (this.companyInfo === undefined) {
        WebsiteApiHelper.get('/customer-company/me/').then((companyInfoRes) => {
          if (companyInfoRes && companyInfoRes.name) {
            this.companyInfo = companyInfoRes;
          }
        });
      }
    },
    fetchCompanyAddresses(currentPage: number = 1) {
      if (this.companyAddressesCurrentPage !== currentPage) {
        this.companyAddressesCurrentPage = currentPage;
      }

      return WebsiteApiHelper.get(`/customer-company-address/?page=${this.companyAddressesCurrentPage}`).then((companyAddressesRes) => {
        if (companyAddressesRes !== undefined && Array.isArray(companyAddressesRes.results)) {
          this.companyAddresses = companyAddressesRes.results;
          this.companyAddressesTotalPages = companyAddressesRes.num_page || 0;
          this.companyAddressesCurrentPage = companyAddressesRes.page || currentPage;
          this.companyAddressesIsEmpty = companyAddressesRes.results.length === 0;
        } else {
          this.companyAddressesIsEmpty = true;
          this.companyAddresses = [];
          this.companyAddressesTotalPages = 0;
        }
      }).catch(() => {
        this.companyAddressesIsEmpty = true;
        this.companyAddresses = [];
        this.companyAddressesTotalPages = 0;
      });
    },
  },
});

export default useUserInfoStore;
